import React from 'react';
import { useQosState } from 'livekit_web/react';
import type { VgpsSession } from 'livekit_web';

export interface QosIndicatorProps {
  session: VgpsSession | null | undefined;
  /** Compact mode: show only the dot, no label. Default false. */
  compact?: boolean;
  style?: React.CSSProperties;
}

const CONFIG = {
  good:     { color: '#4CAF50', label: 'Tốt' },
  degraded: { color: '#FF9800', label: 'Yếu' },
  poor:     { color: '#F44336', label: 'Kém' },
  unknown:  { color: 'rgba(255,255,255,0.4)', label: '…' },
} as const;

/**
 * QosIndicator — shows network quality as a coloured dot (+ optional label).
 *
 * Usage inside a tile overlay:
 *   <QosIndicator session={session} compact />
 *
 * Subscribe to 'network_state' SDK events and refreshes automatically.
 * First measurement appears ~3 s after the session starts playing.
 */
export function QosIndicator({ session, compact = false, style }: QosIndicatorProps): JSX.Element {
  const quality = useQosState(session);
  const { color, label } = CONFIG[quality];

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: compact ? '2px 6px' : '3px 8px',
        borderRadius: 999,
        backgroundColor: 'rgba(8,19,32,0.72)',
        backdropFilter: 'blur(4px)',
        fontSize: 11,
        color: '#fff',
        ...style,
      }}
      title={`Network quality: ${quality}`}
    >
      <style>{`
        @keyframes vgps-qos-pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.25; }
        }
      `}</style>
      <span
        style={{
          width: compact ? 8 : 9,
          height: compact ? 8 : 9,
          borderRadius: '50%',
          backgroundColor: color,
          flexShrink: 0,
          display: 'inline-block',
          boxShadow: quality !== 'unknown' ? `0 0 5px ${color}` : 'none',
          transition: 'background-color 0.5s ease',
          animation: quality !== 'unknown' ? 'vgps-qos-pulse 2s ease-in-out infinite' : 'none',
        }}
      />
      {!compact && <span style={{ transition: 'color 0.3s' }}>{label}</span>}
    </div>
  );
}
